import React, { useState, useEffect, useRef } from 'react';

/**
 * TagFilterBar Component
 *
 * Simplified tag filtering with AND/OR/NOT logic.
 * Always-available: pick a tag from the dropdown, then AND/OR/NOT
 * suggestions appear to continue. AND-connected tags are boxed into a
 * group; OR sits between boxes, so precedence is always visible.
 *
 * Data model (unchanged, keeps backend logic working):
 *   { groups: [{ operator: 'AND', conditions: [{ tagId, tagName, tagColor, not }] }], groupOperator: 'OR' }
 *   The boxes ARE the parentheses: (Tag1 AND Tag2) OR (Tag3 NOT Tag4)
 */

const TagFilterBar = ({ availableTags = [], value, onChange, onManageTags, tagsActive = false }) => {
  // activeInput = null (nothing open) | { mode: 'first' | 'and' | 'or' | 'not' }
  // 'first' is the always-present end input (empty state); the others open when
  // you click the matching operator button. One input, one dropdown — no per-slot pickers.
  const [activeInput, setActiveInput] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const rootRef = useRef(null);
  const queryRef = useRef(null);
  const inputRef = useRef(null);

  const defaultValue = { groups: [], groupOperator: 'OR' };
  const filterValue = value || defaultValue;

  const totalConditions = filterValue.groups.reduce((a, g) => a + (g.conditions?.length || 0), 0);
  const isEmpty = totalConditions === 0;

  useEffect(() => {
    const onDocClick = (e) => {
      if (queryRef.current && !queryRef.current.contains(e.target)) {
        setActiveInput(null);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Focus the input whenever one opens.
  useEffect(() => {
    if (activeInput && inputRef.current) inputRef.current.focus();
  }, [activeInput]);

  const emit = (groups) => onChange({ ...filterValue, groups, groupOperator: 'OR' });

  const openInput = (mode) => { setActiveInput({ mode }); setSearchQuery(''); };
  const closeInput = () => { setActiveInput(null); setSearchQuery(''); };

  // Add a tag using the active input's mode.
  const pickTag = (tag) => {
    if (!activeInput) return;
    const mode = activeInput.mode;
    const groups = filterValue.groups.map(g => ({ ...g, conditions: [...g.conditions] }));

    if (mode === 'or' || mode === 'first' || groups.length === 0) {
      // new OR group
      groups.push({ operator: 'AND', conditions: [{ tagId: tag.id, tagName: tag.name, tagColor: tag.color, not: false }] });
    } else {
      // and / not → append to the last group
      const gi = groups.length - 1;
      if (groups[gi].conditions.some(c => String(c.tagId) === String(tag.id))) { closeInput(); return; }
      groups[gi].conditions.push({ tagId: tag.id, tagName: tag.name, tagColor: tag.color, not: mode === 'not' });
    }
    emit(groups);
    closeInput(); // close after adding one tag
  };

  const removeCondition = (gi, ci) => {
    const groups = filterValue.groups.map(g => ({ ...g, conditions: [...g.conditions] }));
    groups[gi].conditions.splice(ci, 1);
    if (groups[gi].conditions.length === 0) groups.splice(gi, 1);
    emit(groups);
  };

  const toggleNot = (gi, ci) => {
    const groups = filterValue.groups.map(g => ({ ...g, conditions: g.conditions.map(c => ({ ...c })) }));
    groups[gi].conditions[ci].not = !groups[gi].conditions[ci].not;
    emit(groups);
  };

  // Click the OR between group gi-1 and gi → make it AND: merge group gi into gi-1.
  const orToAnd = (gi) => {
    if (gi < 1) return;
    const groups = filterValue.groups.map(g => ({ ...g, conditions: g.conditions.map(c => ({ ...c })) }));
    groups[gi - 1].conditions.push(...groups[gi].conditions);
    groups.splice(gi, 1);
    emit(groups);
  };

  // Click the AND before condition ci in group gi → make it OR: split the group,
  // moving conditions [ci..] into a new group inserted right after gi.
  const andToOr = (gi, ci) => {
    const groups = filterValue.groups.map(g => ({ ...g, conditions: g.conditions.map(c => ({ ...c })) }));
    const moved = groups[gi].conditions.splice(ci);
    groups.splice(gi + 1, 0, { operator: 'AND', conditions: moved });
    emit(groups);
  };

  const clearAll = () => { onChange({ groups: [], groupOperator: 'OR' }); closeInput(); };

  // Tags already used anywhere (avoid duplicates across the whole filter).
  const usedTagIds = new Set(filterValue.groups.flatMap(g => g.conditions.map(c => String(c.tagId))));
  const filteredTags = availableTags.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) && !usedTagIds.has(String(t.id))
  );

  const onInputKeyDown = (e) => {
    if (e.key === 'Escape') { closeInput(); }
    else if (e.key === 'Enter' && filteredTags.length > 0) { pickTag(filteredTags[0]); }
  };

  // Suggestion dropdown — a plain element (NOT a nested component) so the input
  // never remounts and loses focus while typing.
  const suggestions = (
    <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
      <div className="max-h-52 overflow-y-auto py-1">
        {filteredTags.length > 0 ? filteredTags.map(tag => (
          <button
            key={tag.id}
            onMouseDown={(e) => { e.preventDefault(); pickTag(tag); }}
            className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-emerald-50 flex items-center gap-2"
          >
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color || '#6B7280' }} />
            <span className="truncate">{tag.name}</span>
          </button>
        )) : (
          <div className="px-3 py-2 text-sm text-gray-400">{searchQuery ? 'No matching tags' : 'No tags available'}</div>
        )}
      </div>
    </div>
  );

  // The inline text input (shared). Only shows suggestions when active.
  const inputEl = (
    <div ref={queryRef} className="relative inline-flex items-center">
      <input
        ref={inputRef}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyDown={onInputKeyDown}
        onFocus={() => { if (!activeInput) setActiveInput({ mode: 'first' }); }}
        placeholder={isEmpty ? 'query candidates by tag…' : 'type a tag…'}
        className={`h-[22px] text-[12.5px] bg-transparent border-none outline-none placeholder-gray-400 ${isEmpty ? 'min-w-[180px] w-full' : 'min-w-[90px]'}`}
        style={isEmpty ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' } : undefined}
      />
      {activeInput && suggestions}
    </div>
  );


  return (
    <div ref={rootRef} className="bg-white border-b border-gray-200 px-3 h-[40px] flex items-center">
      <div className="flex items-center gap-1.5 flex-nowrap w-full">
        {/* View tabs — All Candidates | Tags (underline tab style, like /firms) */}
        {onManageTags && (
          <>
            <nav className="flex gap-1 h-[40px] -my-px items-stretch flex-shrink-0">
              <button
                onClick={() => { if (tagsActive) onManageTags(); }}
                className={`flex-shrink-0 px-3 text-xs font-medium border-b-2 rounded-none transition-colors flex items-center ${
                  !tagsActive
                    ? 'border-emerald-600 text-emerald-700 font-semibold'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                All Candidates
              </button>
              <button
                onClick={() => { if (!tagsActive) onManageTags(); }}
                className={`flex-shrink-0 px-3 text-xs font-medium border-b-2 rounded-none transition-colors flex items-center gap-1.5 ${
                  tagsActive
                    ? 'border-emerald-600 text-emerald-700 font-semibold'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z" /></svg>
                Tags
              </button>
            </nav>
          </>
        )}

        {tagsActive ? (
          <span className="flex-1" />
        ) : (
        <>
        {/* Query line — full-height terminal-style field: a › caret, the equation,
            then the + and/not/or add-zone. Fills the bar height, flush with the tabs. */}
        <div className="flex items-center gap-1.5 h-[40px] -my-px flex-1 min-w-0 pl-3 self-stretch" style={{ borderLeft: '0.5px solid var(--border-default)' }}>
          {/* prompt caret */}
          <span className="flex-shrink-0 select-none" style={{ color: '#1D9E75', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: '15px', fontWeight: 600 }}>›</span>

          {/* equation body */}
          <div className="flex items-center gap-1.5 min-w-0">

        {/* Empty state — always-present input at the end; type to add the first tag */}
        {isEmpty && inputEl}

        {/* Groups joined by OR */}
        {!isEmpty && filterValue.groups.map((group, gi) => (
          <React.Fragment key={gi}>
            {gi > 0 && (
              <button
                onClick={() => orToAnd(gi)}
                title="OR — click to make AND"
                className="text-[10.5px] font-bold text-blue-700 px-1 py-0.5 rounded tracking-wide flex-shrink-0 hover:bg-blue-50 transition-colors"
              >OR</button>
            )}
            {/* Multi-condition groups get a subtle box so AND-precedence is visible. */}
            <span className={group.conditions.length > 1
              ? 'inline-flex items-center gap-1.5 px-1 py-0.5 rounded-md flex-shrink-0'
              : 'inline-flex items-center gap-1.5 flex-shrink-0'}
              style={group.conditions.length > 1 ? { backgroundColor: 'rgba(83,74,183,0.06)', border: '1px solid rgba(83,74,183,0.13)' } : undefined}
            >
              {group.conditions.map((cond, ci) => (
                <React.Fragment key={ci}>
                  {ci > 0 && (
                    <button
                      onClick={() => andToOr(gi, ci)}
                      title="AND — click to make OR"
                      className="text-[10px] font-bold text-emerald-700 px-1 py-0.5 rounded tracking-wide hover:bg-emerald-50 transition-colors"
                    >AND</button>
                  )}
                  <span
                    className="h-[22px] inline-flex items-center gap-1.5 pl-2 pr-0.5 rounded text-[12.5px] font-medium"
                    style={{ backgroundColor: `${cond.tagColor || '#6B7280'}2e`, color: cond.tagColor || '#374151' }}
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cond.tagColor || '#6B7280' }} />
                    <button
                      onClick={() => toggleNot(gi, ci)}
                      title={cond.not ? 'Excluded — click to include' : 'Included — click to exclude'}
                      className={cond.not ? 'line-through opacity-70' : ''}
                    >
                      {cond.tagName}
                    </button>
                    <button
                      onClick={() => removeCondition(gi, ci)}
                      className="w-4 h-4 inline-flex items-center justify-center rounded opacity-60 hover:opacity-100 hover:bg-black/10"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </span>
                </React.Fragment>
              ))}
            </span>
          </React.Fragment>
        ))}

        {/* Active inline input (opened by clicking an operator) */}
        {!isEmpty && activeInput && (
          <>
            <span className="text-[10px] font-bold px-0.5 tracking-wide select-none" style={{ color: activeInput.mode === 'and' ? '#0F6E56' : activeInput.mode === 'not' ? '#A32D2D' : '#185FA5' }}>
              {activeInput.mode === 'and' ? 'AND' : activeInput.mode === 'not' ? 'NOT' : 'OR'}
            </span>
            {inputEl}
          </>
        )}
          </div>

          {/* Add-zone — click an operator to open an inline input in that mode.
              Hidden while an input is active (you've already chosen). */}
          {!isEmpty && !activeInput && (
            <div className="flex items-center gap-1 flex-shrink-0 ml-1">
              <button onClick={() => openInput('and')}
                className="px-1.5 py-0.5 rounded text-[11px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors">+ and</button>
              <button onClick={() => openInput('not')}
                className="px-1.5 py-0.5 rounded text-[11px] font-medium text-red-700 bg-red-50 hover:bg-red-100 transition-colors">+ not</button>
              <button onClick={() => openInput('or')}
                className="px-1.5 py-0.5 rounded text-[11px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors">+ or</button>
            </div>
          )}
        </div>

        {!isEmpty && (
          <button onClick={clearAll} className="text-[11px] text-gray-400 hover:text-gray-600 flex-shrink-0 pr-3">Clear all</button>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default TagFilterBar;
